<template>
  <div
    class="dh-page flex h-screen min-h-0 w-full flex-col overflow-hidden antialiased md:flex-row"
    :data-theme="theme"
  >
    <PlusAppSidebar />

    <div class="dh-shell relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <div class="dh-shell__dots pointer-events-none absolute inset-0 opacity-90"></div>

      <div class="relative z-10 flex min-h-0 flex-1 flex-col lg:flex-row">
        <!-- 左侧工作流 -->
        <aside class="dh-workflow flex w-full shrink-0 flex-col border-b lg:w-[420px] lg:border-b-0 lg:border-r">
          <header class="dh-workflow__head border-b px-5 py-5">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <h1 class="text-2xl font-extrabold tracking-tight">数字人</h1>
                <p class="dh-text-muted mt-1 text-[13px]">
                  {{ driveMode === 'full' ? '全驱动：人物照片 + 音频 → 自动生成口播视频' : '口型驱动：形象视频 + 克隆配音' }}
                </p>
              </div>
              <button
                type="button"
                class="dh-theme-btn shrink-0"
                :title="theme === 'dark' ? '切换为白色主题' : '切换为暗色主题'"
                @click="toggleTheme"
              >
                <i :class="theme === 'dark' ? 'fas fa-sun' : 'fas fa-moon'"></i>
              </button>
            </div>
            <div class="dh-mode-wrap mt-3 inline-flex rounded-full border p-1">
              <button
                type="button"
                class="dh-mode-btn"
                :class="{ 'dh-mode-btn--active': driveMode === 'full' }"
                @click="driveMode = 'full'"
              >
                全驱动
              </button>
              <button
                type="button"
                class="dh-mode-btn"
                :class="{ 'dh-mode-btn--active': driveMode === 'legacy' }"
                @click="driveMode = 'legacy'"
              >
                口型驱动
              </button>
            </div>
          </header>

          <div class="flex-1 space-y-5 overflow-y-auto px-5 py-5">
            <!-- 1 形象 -->
            <section class="dh-step">
              <div class="dh-step__head">
                <span class="dh-step__badge">1</span>
                <span>{{ driveMode === 'full' ? '选择人物照片' : '选择数字人形象' }}</span>
              </div>
              <div class="mt-3 flex items-center justify-between gap-3">
                <p class="dh-text-success min-w-0 flex-1 truncate text-[12px]">
                  {{ selectedAvatar ? selectedAvatar.name : driveMode === 'full' ? '请上传或选择人物图片' : '请从素材库选择或上传' }}
                </p>
                <button
                  type="button"
                  class="dh-btn-ghost dh-btn-ghost--compact shrink-0"
                  @click="openAvatarModal"
                >
                  选择形象
                </button>
              </div>
              <div
                v-if="selectedAvatarPreviewUrl"
                class="dh-avatar-preview mt-3"
                role="button"
                tabindex="0"
                @click="openAvatarModal"
                @keyup.enter="openAvatarModal"
              >
                <img
                  v-if="!selectedAvatarPreviewIsVideo"
                  :src="selectedAvatarPreviewUrl"
                  :alt="selectedAvatar.name"
                  class="dh-avatar-preview__media"
                />
                <video
                  v-else
                  :src="selectedAvatarPreviewUrl"
                  :poster="selectedAvatar.coverUrl || undefined"
                  class="dh-avatar-preview__media"
                  muted
                  playsinline
                  preload="metadata"
                ></video>
                <span class="dh-avatar-preview__hint">点击更换</span>
              </div>
            </section>

            <!-- 2 音色 -->
            <section class="dh-step">
              <div class="dh-step__head">
                <span class="dh-step__badge">2</span>
                <span>参考音频（音色克隆）</span>
              </div>
              <div class="mt-3 flex items-center justify-between gap-2">
                <p class="dh-text-3 truncate text-[12px]">
                  {{ audioRefName || '上传或选择参考音频' }}
                </p>
                <div class="flex shrink-0 items-center gap-2">
                  <button
                    v-if="audioRefUrl"
                    type="button"
                    class="dh-btn-ghost !px-2 !py-1 text-[11px]"
                    @click="toggleRefAudioPreview"
                  >
                    <i :class="refAudioPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
                    {{ refAudioPlaying ? '暂停' : '试听' }}
                  </button>
                  <button type="button" class="dh-btn-ghost" @click="openAudioModal">选择音频</button>
                </div>
              </div>
            </section>

            <!-- 3 文案 -->
            <section class="dh-step">
              <div class="dh-step__head">
                <span class="dh-step__badge">3</span>
                <span>口播文案</span>
              </div>
              <div class="mt-3 flex flex-col gap-2 sm:flex-row">
                <div class="dh-input-wrap min-w-0 flex-1">
                  <input
                    v-model.trim="extractVideoUrl"
                    type="text"
                    class="dh-input dh-input--clearable"
                    placeholder="粘贴抖音/快手等短视频链接，一键提取口播文案"
                    @keyup.enter="handleExtractScript"
                  />
                  <button
                    v-if="extractVideoUrl"
                    type="button"
                    class="dh-input-wrap__clear"
                    title="清空链接"
                    @click="extractVideoUrl = ''"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <button
                  type="button"
                  class="dh-btn-ai-sm shrink-0 sm:!px-4"
                  :disabled="extractingCopy || !extractVideoUrl"
                  @click="handleExtractScript"
                >
                  <span class="dh-btn-ai-sm__icon">
                    <i :class="extractingCopy ? 'fas fa-spinner fa-spin' : 'fas fa-wand-magic-sparkles'"></i>
                  </span>
                  {{ extractingCopy ? '提取中...' : '提取文案' }}
                </button>
              </div>
              <details
                class="dh-douyin-cookie mt-2"
                :open="douyinCookiePanelOpen"
                @toggle="onDouyinCookiePanelToggle"
              >
                <summary class="dh-douyin-cookie__summary">
                  <i class="fas fa-cookie-bite text-[10px] opacity-70"></i>
                  抖音 Cookie（遇风控时配置，仅存本机）
                  <span v-if="douyinCookieSaved && !douyinCookiePanelOpen" class="dh-text-success">
                    · 已保存
                  </span>
                </summary>
                <div class="mt-2 flex flex-col gap-2" @click.stop>
                  <textarea
                    v-model="douyinCookie"
                    rows="4"
                    class="dh-textarea dh-textarea--cookie text-[11px]"
                    placeholder="粘贴整段 Cookie（从 F12 → Network → 请求头 Cookie 复制，不要只填 sessionid）"
                    @keydown.ctrl.enter.prevent="saveDouyinCookie"
                    @keydown.meta.enter.prevent="saveDouyinCookie"
                  ></textarea>
                  <div class="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      class="dh-btn-ghost !px-2 !py-1 text-[11px]"
                      @click.stop.prevent="saveDouyinCookie"
                    >
                      保存 Cookie
                    </button>
                    <button
                      v-if="douyinCookie"
                      type="button"
                      class="dh-btn-ghost !px-2 !py-1 text-[11px]"
                      @click="clearDouyinCookie"
                    >
                      清除
                    </button>
                    <span
                      class="text-[10px]"
                      :class="douyinCookieSaved ? 'dh-text-success' : 'dh-text-faint'"
                    >
                      {{
                        douyinCookieSaved
                          ? `已保存（${douyinCookieLength} 字符）`
                          : '未配置时将依赖服务端解析'
                      }}
                    </span>
                  </div>
                </div>
              </details>
              <textarea
                v-model.trim="scriptText"
                rows="6"
                maxlength="4000"
                class="dh-textarea mt-3"
                placeholder="输入数字人要说的内容，或粘贴短视频链接后点「提取文案」..."
              ></textarea>
              <div v-if="scriptText.trim()" class="mt-2 flex flex-col gap-2">
                <div class="flex flex-wrap gap-1.5">
                  <button
                    v-for="item in rewriteTypeOptions"
                    :key="item.key"
                    type="button"
                    class="dh-tab !px-2.5 !py-1 text-[11px]"
                    :class="{ 'dh-tab--active': rewriteAction === item.key }"
                    @click="rewriteAction = item.key"
                  >
                    {{ item.label }}
                  </button>
                </div>
                <div class="flex items-center justify-between gap-2">
                  <p class="dh-text-faint text-[11px]">{{ scriptText.length }}/4000</p>
                  <button
                    type="button"
                    class="dh-btn-ai-sm dh-btn-ai-sm--rewrite shrink-0"
                    :disabled="rewritingCopy || !rewriteAction"
                    @click="handleRewriteScript"
                  >
                    <span class="dh-btn-ai-sm__icon">
                      <i :class="rewritingCopy ? 'fas fa-spinner fa-spin' : 'fas fa-pen-fancy'"></i>
                    </span>
                    {{ rewritingCopy ? '改写中...' : '改写文案' }}
                    <span v-if="rewriteCreditsCost > 0" class="dh-btn-ai-sm__credits">{{ rewriteCreditsCost }} 积分</span>
                  </button>
                </div>
              </div>
              <p v-else class="dh-text-faint mt-1 text-right text-[11px]">{{ scriptText.length }}/4000</p>
            </section>

            <!-- 全驱动：动作描述 + 模式 -->
            <section v-if="driveMode === 'full'" class="dh-step">
              <div class="dh-step__head">
                <span class="dh-step__badge">+</span>
                <span>全驱动参数</span>
              </div>
              <textarea
                v-model.trim="fullDrivePrompt"
                rows="2"
                maxlength="500"
                class="dh-textarea mt-3"
                placeholder="可选：人物面对镜头自然说话，表情稳定，肢体动作适度"
              ></textarea>
              <div class="mt-3 flex gap-2">
                <button
                  type="button"
                  class="dh-tab flex-1"
                  :class="{ 'dh-tab--active': fullDriveMode === 'standard' }"
                  @click="fullDriveMode = 'standard'"
                >
                  标准 quality
                </button>
                <button
                  type="button"
                  class="dh-tab flex-1"
                  :class="{ 'dh-tab--active': fullDriveMode === 'fast' }"
                  @click="fullDriveMode = 'fast'"
                >
                  快速 fast
                </button>
              </div>
            </section>

            <!-- 4 配音 -->
            <section class="dh-step">
              <div class="dh-step__head">
                <span class="dh-step__badge">4</span>
                <span>生成配音</span>
              </div>
              <button
                type="button"
                class="dh-btn-ai dh-btn-ai--audio mt-3 w-full"
                :disabled="audioLoading || !canSynthAudio"
                @click="handleSynthAudio"
              >
                <span class="dh-btn-ai__icon">
                  <i v-if="audioLoading" class="fas fa-spinner fa-spin"></i>
                  <svg v-else class="dh-btn-ai__svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="3" y="9" width="2.5" height="6" rx="1.25" fill="currentColor" />
                    <rect x="7.5" y="6" width="2.5" height="12" rx="1.25" fill="currentColor" />
                    <rect x="12" y="4" width="2.5" height="16" rx="1.25" fill="currentColor" />
                    <rect x="16.5" y="7" width="2.5" height="10" rx="1.25" fill="currentColor" />
                    <rect x="21" y="10" width="2.5" height="4" rx="1.25" fill="currentColor" opacity="0.85" />
                  </svg>
                </span>
                <span class="dh-btn-ai__label">{{ audioLoading ? '合成中...' : '文案转音频' }}</span>
                <span v-if="voiceCreditsCost > 0" class="dh-btn-ai__credits">{{ voiceCreditsCost }} 积分</span>
                <span class="dh-btn-ai__tag">AI</span>
              </button>
              <div v-if="generatedAudioUrl" class="mt-3 flex items-center justify-between text-[12px]">
                <span class="dh-text-muted">配音已生成</span>
                <button type="button" class="dh-btn-ghost" @click="toggleAudioPreview">
                  <i :class="audioPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
                  {{ audioPlaying ? '暂停' : '试听' }}
                </button>
              </div>
            </section>

            <!-- 5 视频 -->
            <section class="dh-step">
              <div class="dh-step__head">
                <span class="dh-step__badge">5</span>
                <span>生成数字人视频</span>
              </div>
              <button
                type="button"
                class="dh-btn-ai dh-btn-ai--video mt-3 w-full"
                :disabled="videoLoading || !canGenerateVideo"
                @click="handleGenerateVideo"
              >
                <span class="dh-btn-ai__icon">
                  <i v-if="videoLoading" class="fas fa-spinner fa-spin"></i>
                  <svg v-else class="dh-btn-ai__svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" stroke-width="1.8" />
                    <path d="M3 9h18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                    <path d="M8 5V3.5A1.5 1.5 0 0 1 9.5 2h5A1.5 1.5 0 0 1 16 3.5V5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                    <circle cx="12" cy="13" r="3.2" stroke="currentColor" stroke-width="1.8" />
                    <circle cx="12" cy="13" r="1.1" fill="currentColor" />
                  </svg>
                </span>
                <span class="dh-btn-ai__label">
                  {{ videoLoading ? generateProgress || '生成中...' : driveMode === 'full' ? '提交全驱动任务' : '开始生成' }}
                </span>
                <span v-if="driveMode === 'full' && fullDriveCreditsCost > 0" class="dh-btn-ai__credits">
                  <template v-if="generatedAudioDurationSec > 0">
                    {{ fullDriveCreditsCost }} 积分（{{ generatedAudioDurationSec }}秒×{{ fullDrivePerSecCost }}）
                  </template>
                  <template v-else>{{ fullDrivePerSecCost }} 积分/秒</template>
                </span>
                <span class="dh-btn-ai__tag">AI</span>
              </button>
              <p v-if="videoLoading && currentTaskId" class="dh-text-faint mt-2 text-[11px]">
                task_id: {{ currentTaskId }}
              </p>
              <p v-if="lastResultUrl" class="dh-text-success mt-3 text-[12px]">
                最新结果已生成，可在右侧预览
              </p>
            </section>
          </div>
        </aside>

        <!-- 右侧项目 -->
        <main class="flex min-h-0 min-w-0 flex-1 flex-col">
          <header class="dh-projects-head">
            <div class="dh-projects-head__title">
              <h2>我的数字人作品</h2>
              <p>仅显示当前登录账号的作品，生成任务提交后可在列表查看</p>
            </div>
            <div class="dh-projects-toolbar">
              <div class="dh-projects-search">
                <i class="fas fa-search dh-projects-search__icon"></i>
                <input
                  v-model.trim="projectSearch"
                  type="search"
                  class="dh-projects-search__input"
                  placeholder="搜索标题或任务 ID"
                />
                <button
                  v-if="projectSearch"
                  type="button"
                  class="dh-projects-search__clear"
                  @click="projectSearch = ''"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <div class="dh-projects-toolbar__actions">
                <button
                  v-for="t in projectTabs"
                  :key="t.key"
                  type="button"
                  class="dh-tab"
                  :class="{ 'dh-tab--active': projectTab === t.key }"
                  @click="projectTab = t.key"
                >
                  {{ t.label }}
                </button>
                <button
                  type="button"
                  class="dh-btn-ghost dh-btn-ghost--compact"
                  title="刷新列表"
                  @click="refreshProjects"
                >
                  <i :class="tasksBootstrapping ? 'fas fa-spinner fa-spin' : 'fas fa-rotate-right'"></i>
                </button>
              </div>
            </div>
          </header>

          <div class="flex-1 overflow-y-auto p-5">
            <div
              v-if="tasksBootstrapping && !searchedProjects.length"
              class="dh-projects-empty"
            >
              <i class="fas fa-spinner fa-spin mr-2"></i>加载中...
            </div>
            <div v-else-if="!loggedIn" class="dh-projects-empty">
              <i class="fas fa-user-lock dh-projects-empty__icon"></i>
              <p>登录后查看属于您的数字人作品</p>
            </div>
            <div v-else-if="!filteredProjects.length" class="dh-projects-empty">
              <i class="fas fa-clapperboard dh-projects-empty__icon"></i>
              <p>暂无作品，完成左侧流程后在此查看</p>
            </div>
            <div v-else-if="!searchedProjects.length" class="dh-projects-empty">
              <i class="fas fa-search dh-projects-empty__icon"></i>
              <p>未找到匹配「{{ projectSearch }}」的作品</p>
            </div>
            <div v-else class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              <article
                v-for="p in searchedProjects"
                :key="p.id || p.taskId"
                class="dh-project-card group"
              >
                <div class="dh-project-card__media">
                  <span
                    class="dh-project-card__status"
                    :class="projectStatusClass(p)"
                  >
                    <i class="dh-project-card__status-dot"></i>
                    {{ projectStatusText(p) }}
                  </span>
                  <button
                    type="button"
                    class="dh-project-card__delete"
                    title="删除作品"
                    @click.stop="deleteProject(p)"
                  >
                    <i class="fas fa-trash-can"></i>
                  </button>
                  <video
                    v-if="p.shuzirenUrl"
                    :src="p.shuzirenUrl"
                    class="dh-project-card__thumb"
                    muted
                    playsinline
                    preload="metadata"
                  ></video>
                  <img
                    v-else-if="p.cover"
                    :src="p.cover"
                    alt=""
                    class="dh-project-card__thumb dh-project-card__thumb--cover"
                  />
                  <div v-else class="dh-project-card__placeholder">
                    <i class="fas fa-user-astronaut"></i>
                  </div>
                  <div class="dh-project-card__shade"></div>
                  <div
                    v-if="p.shuzirenUrl"
                    class="dh-project-card__overlay"
                  >
                    <button type="button" class="dh-project-card__preview" @click="previewVideo(p.shuzirenUrl)">
                      <span class="dh-project-card__play">
                        <i class="fas fa-play"></i>
                      </span>
                      <span class="dh-project-card__preview-text">预览视频</span>
                    </button>
                  </div>
                  <div
                    v-else-if="isProjectGenerating(p)"
                    class="dh-project-card__overlay dh-project-card__overlay--loading"
                  >
                    <div class="dh-project-card__loading">
                      <i class="fas fa-spinner fa-spin"></i>
                      <span>生成中</span>
                    </div>
                  </div>
                </div>
                <div class="dh-project-card__body">
                  <h3 class="dh-project-card__title" :title="p.title">{{ p.title }}</h3>
                  <div class="dh-project-card__footer">
                    <p class="dh-project-card__meta">
                      <i class="far fa-clock"></i>
                      {{ formatProjectTime(p.time || p.updatedAt || p.createdAt) }}
                    </p>
                    <button
                      v-if="p.shuzirenUrl"
                      type="button"
                      class="dh-project-card__action"
                      @click="previewVideo(p.shuzirenUrl)"
                    >
                      <i class="fas fa-play"></i>
                      播放
                    </button>
                    <button
                      v-else-if="isProjectGenerating(p) || projectNeedsSync(p)"
                      type="button"
                      class="dh-project-card__action dh-project-card__action--sync"
                      :disabled="!!projectSyncingMap[p.taskId]"
                      @click="refreshProjectStatus(p)"
                    >
                      <i :class="projectSyncingMap[p.taskId] ? 'fas fa-spinner fa-spin' : 'fas fa-rotate-right'"></i>
                      同步状态
                    </button>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </main>
      </div>
    </div>

    <!-- 形象选择 -->
    <div v-if="avatarModal" class="dh-modal" @click.self="avatarModal = false">
      <div class="dh-modal__panel">
        <div class="dh-modal__head">
          <h3>{{ driveMode === 'full' ? '人物照片' : '数字人形象' }}</h3>
          <button type="button" @click="avatarModal = false"><i class="fas fa-times"></i></button>
        </div>
        <div class="dh-modal__body">
          <div v-if="avatarsLoading" class="dh-text-muted py-10 text-center"><i class="fas fa-spinner fa-spin"></i></div>
          <div v-else class="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <button type="button" class="dh-asset-card dh-asset-card--create" @click="triggerAvatarUpload">
              <i class="fas fa-plus text-xl"></i>
              <span>{{ driveMode === 'full' ? '上传照片' : '上传形象' }}</span>
            </button>
            <button
              v-for="a in avatars"
              :key="a.id"
              type="button"
              class="dh-asset-card"
              :class="{ 'dh-asset-card--active': selectedAvatar && selectedAvatar.id === a.id }"
              @click="selectAvatar(a)"
            >
              <img
                v-if="driveMode === 'full' && (a.imageUrl || a.coverUrl)"
                :src="a.imageUrl || a.coverUrl"
                alt=""
                class="h-full w-full object-cover"
              />
              <video v-else-if="a.videoUrl" :src="a.videoUrl" class="h-full w-full object-cover" muted playsinline></video>
              <img v-else-if="a.coverUrl" :src="a.coverUrl" alt="" class="h-full w-full object-cover" />
              <span class="dh-asset-card__name">{{ a.name }}</span>
            </button>
          </div>
        </div>
        <input
          ref="avatarFileInput"
          type="file"
          :accept="driveMode === 'full' ? 'image/*' : 'video/*'"
          class="hidden"
          @change="onAvatarFile"
        />
      </div>
    </div>

    <!-- 音频选择 -->
    <div v-if="audioModal" class="dh-modal" @click.self="closeAudioModal">
      <div class="dh-modal__panel">
        <div class="dh-modal__head">
          <h3>参考音频</h3>
          <button type="button" @click="closeAudioModal"><i class="fas fa-times"></i></button>
        </div>
        <div class="dh-modal__body">
          <div v-if="audioListLoading" class="dh-text-muted py-10 text-center"><i class="fas fa-spinner fa-spin"></i></div>
          <div v-else class="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <button type="button" class="dh-asset-card dh-asset-card--create" :disabled="audioUploading" @click="triggerAudioUpload">
              <i :class="audioUploading ? 'fas fa-spinner fa-spin text-xl' : 'fas fa-upload text-xl'"></i>
              <span>{{ audioUploading ? '上传中...' : '上传音频' }}</span>
            </button>
            <div
              v-for="a in audioAssets"
              :key="a.id"
              class="dh-asset-card dh-asset-card--audio"
              :class="{ 'dh-asset-card--active': audioRefUrl === a.url }"
              role="button"
              tabindex="0"
              @click="selectAudio(a)"
              @keyup.enter="selectAudio(a)"
            >
              <i class="fas fa-music text-2xl text-sky-300"></i>
              <span class="dh-asset-card__name">{{ a.name }}</span>
              <button
                v-if="a.url"
                type="button"
                class="dh-audio-preview-btn"
                @click.stop="toggleAudioAssetPreview(a)"
              >
                {{ audioAssetPlayingId === a.id && isAudioAssetPlaying ? '暂停' : '试听' }}
              </button>
            </div>
          </div>
        </div>
        <input ref="audioFileInput" type="file" accept="audio/*" class="hidden" @change="onAudioFile" />
      </div>
    </div>

    <!-- 预览 -->
    <div v-if="previewUrl" class="dh-modal" @click.self="previewUrl = ''">
      <div class="dh-modal__panel dh-modal__panel--video">
        <button type="button" class="dh-preview-close" @click="previewUrl = ''"><i class="fas fa-times"></i></button>
        <div class="dh-preview-video-wrap">
          <video
            :src="previewUrl"
            controls
            autoplay
            playsinline
            class="dh-preview-video"
          ></video>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PlusAppSidebar from '../components/PlusAppSidebar.vue'
import {
  fetchDigitalHumanAvatars,
  fetchDigitalHumanPortraitImages,
  fetchDigitalHumanAudioLibrary,
  createDigitalHumanVideo,
  submitImageHumanFullDrive,
  uploadDigitalHumanAvatarVideo,
  uploadDigitalHumanPortraitImage,
  uploadReferenceAudioFile,
  deleteImageHumanTaskRecord,
  queryImageHumanTask
} from '../utils/digitalHumanApi'
import {
  loadDigitalHumanTasks,
  upsertDigitalHumanTask,
  removeDigitalHumanTask,
  mergeProjectLists,
  bootstrapDigitalHumanTasks,
  pullDigitalHumanTasksFromServer,
  resumeTaskPolling,
  statusLabel,
  isTaskInProgress,
  sanitizeVideoResultUrl,
  getCurrentUserId
} from '../utils/digitalHumanTaskStore'
import {
  loadDigitalHumanModelCatalog,
  rewriteCredits,
  voiceCloneCredits,
  fullDriveCredits,
  fullDriveCreditsPerSecond,
  probeAudioDurationSec,
  assertDigitalHumanCredits
} from '../utils/digitalHumanCredits'
import { synthesizeClonedVoice } from '../utils/nodeAudioTts'
import { extractShortVideoCopy, extractFirstHttpUrl } from '../utils/videoCopyExtractApi'
import { getDouyinCookie, setDouyinCookie, normalizeDouyinCookieInput } from '../utils/douyinCookieStore'
import { REWRITE_TYPES, rewriteVideoCopy } from '../utils/videoCopyRewriteApi'
import { showSuccessToast, showErrorToast } from '../utils/toast'
import { loginGate, hasAuthToken, isLoggedInUser } from '../utils/loginGate'

const DEFAULT_AUDIO = {
  url: 'https://sf6-cdn-tos.douyinstatic.com/obj/ies-music/7617560274899454762.mp3',
  name: '示例音色（女声）'
}

export default {
  name: 'DigitalHuman',
  components: { PlusAppSidebar },
  data() {
    return {
      theme: (localStorage.getItem('theme') || 'dark') === 'light' ? 'light' : 'dark',
      driveMode: 'full',
      fullDrivePrompt: '人物面对镜头自然说话，表情稳定，肢体动作适度',
      fullDriveMode: 'standard',

      selectedAvatar: null,
      avatars: [],
      avatarsLoading: false,
      avatarModal: false,
      avatarUploading: false,

      audioRefUrl: DEFAULT_AUDIO.url,
      audioRefName: DEFAULT_AUDIO.name,
      audioAssets: [{ id: 'demo', name: DEFAULT_AUDIO.name, url: DEFAULT_AUDIO.url }],
      audioModal: false,
      audioListLoading: false,
      audioUploading: false,

      scriptText: '',
      extractVideoUrl: '',
      extractingCopy: false,
      rewriteAction: 'polish',
      rewritingCopy: false,
      rewriteTypeOptions: REWRITE_TYPES,
      douyinCookie: '',
      douyinCookieSaved: false,
      douyinCookiePanelOpen: false,
      generatedAudioUrl: '',
      generatedAudioDurationSec: 0,
      audioLoading: false,
      audioPlaying: false,
      audioInstance: null,
      refAudioPlaying: false,
      refAudioInstance: null,
      audioAssetPlayingId: null,
      isAudioAssetPlaying: false,
      audioAssetPreviewInstance: null,

      videoLoading: false,
      lastResultUrl: '',
      currentTaskId: '',
      generateProgress: '',
      localTasks: [],

      projectTab: 'all',
      projectSearch: '',
      projectSyncingMap: {},
      projectTabs: [
        { key: 'all', label: '全部' },
        { key: '0', label: '生成中' },
        { key: '1', label: '已完成' }
      ],
      tasksBootstrapping: false,
      modelCatalog: [],

      tasksUserId: '',

      previewUrl: '',
      loginGate
    }
  },
  computed: {
    loggedIn() {
      return isLoggedInUser()
    },
    rewriteCreditsCost() {
      return rewriteCredits(this.modelCatalog)
    },
    voiceCreditsCost() {
      return voiceCloneCredits(this.modelCatalog)
    },
    fullDriveCreditsCost() {
      return fullDriveCredits(
        this.modelCatalog,
        this.fullDriveMode,
        this.generatedAudioDurationSec
      )
    },
    fullDrivePerSecCost() {
      return fullDriveCreditsPerSecond(this.modelCatalog, this.fullDriveMode)
    },
    canSynthAudio() {
      return !!(this.audioRefUrl && this.scriptText.trim())
    },
    canGenerateVideo() {
      if (!this.generatedAudioUrl || !this.scriptText.trim()) return false
      if (this.driveMode === 'full') {
        return !!(this.selectedAvatar && (this.selectedAvatar.imageUrl || this.selectedAvatar.coverUrl))
      }
      return !!(this.selectedAvatar && this.selectedAvatar.videoUrl)
    },
    filteredProjects() {
      return mergeProjectLists(this.localTasks, [], this.projectTab)
    },
    searchedProjects() {
      const q = String(this.projectSearch || '').trim().toLowerCase()
      const list = this.filteredProjects
      if (!q) return list
      return list.filter((p) => {
        const title = String(p.title || '').toLowerCase()
        const taskId = String(p.taskId || '').toLowerCase()
        const time = String(p.time || p.updatedAt || p.createdAt || '').toLowerCase()
        return title.includes(q) || taskId.includes(q) || time.includes(q)
      })
    },
    douyinCookieLength() {
      return String(this.douyinCookie || '').length
    },
    selectedAvatarPreviewUrl() {
      const a = this.selectedAvatar
      if (!a) return ''
      if (this.driveMode === 'full') {
        return a.imageUrl || a.coverUrl || ''
      }
      if (a.coverUrl) return a.coverUrl
      return a.videoUrl || a.imageUrl || ''
    },
    selectedAvatarPreviewIsVideo() {
      const a = this.selectedAvatar
      if (!a || this.driveMode === 'full') return false
      return !!(a.videoUrl && !a.coverUrl)
    }
  },
  watch: {
    driveMode() {
      this.selectedAvatar = null
    },
    'loginGate.visible'(visible) {
      if (!visible && hasAuthToken()) {
        this.tasksUserId = getCurrentUserId()
        this.bootstrapTasks()
      }
    }
  },
  created() {
    this.tasksUserId = getCurrentUserId()
    this.localTasks = isLoggedInUser() ? loadDigitalHumanTasks() : []
    const saved = getDouyinCookie()
    this.douyinCookie = saved
    this.douyinCookieSaved = !!saved
  },
  mounted() {
    this.applyTheme(this.theme)
    this.bootstrapTasks()
    this.loadModelPricing()
    window.addEventListener('storage', this.onAuthStorageChange)
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.onAuthStorageChange)
    this.stopAllAudioPlayback()
    this._destroyed = true
  },
  methods: {
    statusLabel,
    applyTheme(next) {
      const theme = next === 'light' ? 'light' : 'dark'
      this.theme = theme
      try {
        localStorage.setItem('theme', theme)
      } catch {
        /* ignore */
      }
      if (typeof document !== 'undefined' && document.body) {
        document.body.setAttribute('data-theme', theme)
      }
    },
    toggleTheme() {
      this.applyTheme(this.theme === 'dark' ? 'light' : 'dark')
    },
    async loadModelPricing() {
      try {
        this.modelCatalog = await loadDigitalHumanModelCatalog(true)
      } catch (e) {
        this.modelCatalog = []
      }
    },
    onAuthStorageChange(e) {
      if (!e || (e.key !== 'user' && e.key !== 'token')) return
      const next = getCurrentUserId()
      if (next === this.tasksUserId) return
      this.tasksUserId = next
      this.bootstrapTasks()
    },
    onTasksUpdated(tasks) {
      if (this._destroyed) return
      if (!isLoggedInUser()) {
        this.localTasks = []
        return
      }
      const next = Array.isArray(tasks) ? tasks : loadDigitalHumanTasks()
      // 避免轮询/拉取回调用空列表盖掉刚提交的本地任务
      if ((!next || !next.length) && this.localTasks && this.localTasks.length) {
        this.localTasks = loadDigitalHumanTasks()
      } else {
        this.localTasks = next
      }
      const latest = this.localTasks.find(
        (t) =>
          t.status === 'completed' &&
          sanitizeVideoResultUrl(t.resultUrl || t.shuzirenUrl, t.cover)
      )
      if (latest && latest.resultUrl) {
        this.lastResultUrl = latest.resultUrl
      }
    },
    async bootstrapTasks() {
      if (!isLoggedInUser()) {
        this.localTasks = []
        return
      }
      this.tasksUserId = getCurrentUserId()
      this.tasksBootstrapping = true
      try {
        await bootstrapDigitalHumanTasks({
          onUpdate: (tasks) => this.onTasksUpdated(tasks)
        })
      } catch (e) {
        this.localTasks = loadDigitalHumanTasks()
      } finally {
        this.tasksBootstrapping = false
      }
    },
    refreshProjects() {
      this.bootstrapTasks()
    },

    formatProjectTime(raw) {
      const s = String(raw || '').trim()
      if (!s) return '—'
      const d = new Date(s)
      if (Number.isNaN(d.getTime())) return s
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },

    projectStatusText(p) {
      if (p.shuzirenUrl) return '已完成'
      const s = String(p._status || '').toLowerCase()
      if (s === 'completed') return '已完成'
      return statusLabel(p._status)
    },

    isProjectGenerating(p) {
      if (p.shuzirenUrl) return false
      const s = String(p._status || '').toLowerCase()
      if (s === 'completed' || s === 'failed') return false
      return isTaskInProgress(s) || s === 'processing' || s === 'pending' || !s
    },

    projectNeedsSync(p) {
      if (p.shuzirenUrl || !p.taskId) return false
      const s = String(p._status || '').toLowerCase()
      return s === 'completed' || isTaskInProgress(s) || s === 'processing' || s === 'pending' || !s
    },

    projectStatusClass(p) {
      if (p.shuzirenUrl) return 'dh-project-card__status--done'
      const s = String(p._status || '').toLowerCase()
      if (s === 'failed') return 'dh-project-card__status--fail'
      if (s === 'completed') return 'dh-project-card__status--done'
      return 'dh-project-card__status--pending'
    },

    async refreshProjectStatus(p) {
      const taskId = String(p.taskId || '').trim()
      if (!taskId) return
      this.$set(this.projectSyncingMap, taskId, true)
      try {
        const info = await queryImageHumanTask(taskId, {
          excludeUrls: [p.cover].filter(Boolean)
        })
        this.localTasks = upsertDigitalHumanTask({
          taskId,
          cover: p.cover || undefined,
          status: info.resultUrl ? 'completed' : info.status,
          resultUrl: info.resultUrl || undefined,
          error: info.error || undefined
        })
        if (info.resultUrl) {
          showSuccessToast('已同步，视频可播放')
        } else if (info.status === 'completed') {
          showSuccessToast('任务已完成，视频地址同步中')
        } else if (info.status === 'failed') {
          showErrorToast(info.error || '任务失败')
        } else {
          showSuccessToast('仍在生成中，请稍后再试')
        }
      } catch (e) {
        showErrorToast(e.message || '同步失败')
      } finally {
        this.$set(this.projectSyncingMap, taskId, false)
      }
    },

    async deleteProject(p) {
      const taskId = String(p.taskId || (p.id && String(p.id).replace(/^local-/, '')) || '').trim()
      if (!taskId) {
        showErrorToast('无法识别任务 ID')
        return
      }
      const title = String(p.title || '该作品').trim()
      if (!window.confirm(`确定删除「${title}」？此操作不可恢复。`)) return
      try {
        this.localTasks = removeDigitalHumanTask(taskId)
        await deleteImageHumanTaskRecord(taskId).catch(() => {})
        if (this.currentTaskId === taskId) {
          this.currentTaskId = ''
          this.videoLoading = false
        }
        showSuccessToast('已删除作品')
      } catch (e) {
        showErrorToast(e.message || '删除失败')
      }
    },

    async openAvatarModal() {
      this.avatarModal = true
      this.avatarsLoading = true
      try {
        if (this.driveMode === 'full') {
          this.avatars = await fetchDigitalHumanPortraitImages()
        } else {
          this.avatars = await fetchDigitalHumanAvatars()
        }
      } catch (e) {
        showErrorToast(e.message || '加载形象失败')
        this.avatars = []
      } finally {
        this.avatarsLoading = false
      }
    },
    selectAvatar(a) {
      this.selectedAvatar = a
      this.avatarModal = false
      showSuccessToast('已选择：' + a.name)
    },
    triggerAvatarUpload() {
      this.$refs.avatarFileInput && this.$refs.avatarFileInput.click()
    },
    async onAvatarFile(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      if (this.driveMode === 'full') {
        if (!file.type.startsWith('image/')) {
          showErrorToast('请选择图片文件')
          return
        }
      } else if (!file.type.startsWith('video/')) {
        showErrorToast('请选择视频文件')
        return
      }
      this.avatarUploading = true
      try {
        const item =
          this.driveMode === 'full'
            ? await uploadDigitalHumanPortraitImage(file)
            : await uploadDigitalHumanAvatarVideo(file)
        const keyUrl = this.driveMode === 'full' ? item.imageUrl || item.coverUrl : item.videoUrl
        this.avatars = [item, ...this.avatars.filter((x) => {
          const xUrl = this.driveMode === 'full' ? x.imageUrl || x.coverUrl : x.videoUrl
          return x.id !== item.id && xUrl !== keyUrl
        })]
        if (this.driveMode === 'full') {
          showSuccessToast('已上传并保存到图库')
        }
        this.selectAvatar(item)
      } catch (err) {
        showErrorToast(err.message || '上传失败')
      } finally {
        this.avatarUploading = false
      }
    },

    closeAudioModal() {
      this.stopAudioAssetPreview()
      this.audioModal = false
    },
    async openAudioModal() {
      this.stopAllAudioPlayback()
      this.audioModal = true
      this.audioListLoading = true
      try {
        const library = await fetchDigitalHumanAudioLibrary()
        const demo = { id: 'demo', name: DEFAULT_AUDIO.name, url: DEFAULT_AUDIO.url }
        const seen = new Set()
        const merged = []
        ;[...library, demo].forEach((a) => {
          if (!a || !a.url || seen.has(a.url)) return
          seen.add(a.url)
          merged.push(a)
        })
        this.audioAssets = merged
      } catch (e) {
        showErrorToast(e.message || '加载音频库失败')
        this.audioAssets = [{ id: 'demo', name: DEFAULT_AUDIO.name, url: DEFAULT_AUDIO.url }]
      } finally {
        this.audioListLoading = false
      }
    },
    selectAudio(a) {
      this.stopAudioAssetPreview()
      this.audioRefUrl = a.url
      this.audioRefName = a.name
      this.audioModal = false
      showSuccessToast('已选择音频：' + a.name)
    },
    triggerAudioUpload() {
      this.$refs.audioFileInput && this.$refs.audioFileInput.click()
    },
    async onAudioFile(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      if (!file.type.startsWith('audio/')) {
        showErrorToast('请选择音频文件')
        return
      }
      this.audioUploading = true
      try {
        const item = await uploadReferenceAudioFile(file)
        this.audioAssets = [item, ...this.audioAssets.filter((x) => x.id !== item.id && x.url !== item.url)]
        this.selectAudio(item)
        showSuccessToast('已上传并保存到音频库')
      } catch (err) {
        showErrorToast(err.message || '音频上传失败')
      } finally {
        this.audioUploading = false
      }
    },

    async handleExtractScript() {
      if (this.extractingCopy) return
      this.saveDouyinCookie()
      const raw = String(this.extractVideoUrl || this.scriptText || '').trim()
      const url = extractFirstHttpUrl(raw) || raw
      if (!url) {
        showErrorToast('请输入短视频分享链接')
        return
      }
      this.extractingCopy = true
      try {
        const result = await extractShortVideoCopy(url)
        this.scriptText = String(result.text || '').slice(0, 4000)
        this.generatedAudioUrl = ''
        this.generatedAudioDurationSec = 0
        showSuccessToast('文案提取成功')
      } catch (e) {
        showErrorToast(e.message || '文案提取失败，请检查链接是否有效')
      } finally {
        this.extractingCopy = false
      }
    },

    async handleRewriteScript() {
      if (this.rewritingCopy) return
      const text = String(this.scriptText || '').trim()
      if (!text) {
        showErrorToast('没有可改写的文案')
        return
      }
      if (!this.rewriteAction) {
        showErrorToast('请选择改写类型')
        return
      }
      this.rewritingCopy = true
      try {
        await assertDigitalHumanCredits(this.rewriteCreditsCost, '文案改写')
        const rewritten = await rewriteVideoCopy(text, this.rewriteAction)
        this.scriptText = rewritten.slice(0, 4000)
        this.generatedAudioUrl = ''
        this.generatedAudioDurationSec = 0
        showSuccessToast('文案改写成功')
      } catch (e) {
        showErrorToast(e.message || '文案改写失败，请稍后重试')
      } finally {
        this.rewritingCopy = false
      }
    },

    saveDouyinCookie() {
      const normalized = normalizeDouyinCookieInput(this.douyinCookie)
      if (!normalized) {
        showErrorToast('请先粘贴 Cookie 内容')
        this.douyinCookieSaved = false
        return
      }
      const result = setDouyinCookie(normalized)
      if (!result.ok) {
        this.douyinCookieSaved = false
        showErrorToast(result.error || 'Cookie 保存失败')
        return
      }
      const persisted = getDouyinCookie()
      if (persisted !== normalized) {
        this.douyinCookieSaved = false
        showErrorToast('Cookie 保存后读取校验失败，请更换浏览器或关闭无痕模式')
        return
      }
      this.douyinCookie = persisted
      this.douyinCookieSaved = true
      const hint = result.storage === 'session' ? '（会话级存储）' : ''
      showSuccessToast(`Cookie 已保存${hint}，共 ${persisted.length} 字符`)
      this.douyinCookiePanelOpen = false
    },

    onDouyinCookiePanelToggle(e) {
      this.douyinCookiePanelOpen = !!(e && e.target && e.target.open)
    },

    clearDouyinCookie() {
      this.douyinCookie = ''
      setDouyinCookie('')
      this.douyinCookieSaved = false
      showSuccessToast('已清除抖音 Cookie')
    },

    async handleSynthAudio() {
      if (this.audioLoading || !this.canSynthAudio) return
      this.audioLoading = true
      this.stopAllAudioPlayback()
      this.generatedAudioUrl = ''
      this.generatedAudioDurationSec = 0
      try {
        await assertDigitalHumanCredits(this.voiceCreditsCost, '文案转音频')
        const url = await synthesizeClonedVoice({
          referenceAudioUrl: this.audioRefUrl,
          text: this.scriptText.trim()
        })
        this.generatedAudioUrl = url
        this.generatedAudioDurationSec = await probeAudioDurationSec(url)
        showSuccessToast(
          this.generatedAudioDurationSec > 0
            ? `配音生成成功（约 ${this.generatedAudioDurationSec} 秒）`
            : '配音生成成功'
        )
      } catch (e) {
        showErrorToast(e.message || '配音生成失败')
      } finally {
        this.audioLoading = false
      }
    },
    toggleAudioPreview() {
      if (!this.generatedAudioUrl) return
      if (this.audioPlaying && this.audioInstance) {
        this.stopAudioPreview()
        return
      }
      this.stopRefAudioPreview()
      this.stopAudioAssetPreview()
      try {
        this.audioInstance = new Audio(this.generatedAudioUrl)
        this.audioInstance.onended = () => {
          this.audioPlaying = false
          this.audioInstance = null
        }
        this.audioInstance.play().then(() => {
          this.audioPlaying = true
        }).catch(() => {
          showErrorToast('无法播放音频')
        })
      } catch (e) {
        showErrorToast('无法播放音频')
      }
    },
    toggleRefAudioPreview() {
      if (!this.audioRefUrl) return
      if (this.refAudioPlaying && this.refAudioInstance) {
        this.stopRefAudioPreview()
        return
      }
      this.stopAudioPreview()
      this.stopAudioAssetPreview()
      try {
        this.refAudioInstance = new Audio(this.audioRefUrl)
        this.refAudioInstance.onended = () => {
          this.refAudioPlaying = false
          this.refAudioInstance = null
        }
        this.refAudioInstance.play().then(() => {
          this.refAudioPlaying = true
        }).catch(() => {
          showErrorToast('参考音频试听失败')
        })
      } catch (e) {
        showErrorToast('无法播放参考音频')
      }
    },
    toggleAudioAssetPreview(a) {
      const url = a && a.url
      if (!url) return
      if (this.isAudioAssetPlaying && this.audioAssetPlayingId === a.id && this.audioAssetPreviewInstance) {
        this.stopAudioAssetPreview()
        return
      }
      this.stopAudioPreview()
      this.stopRefAudioPreview()
      try {
        if (this.audioAssetPreviewInstance) {
          this.audioAssetPreviewInstance.pause()
          this.audioAssetPreviewInstance = null
        }
        const audio = new Audio(url)
        this.audioAssetPreviewInstance = audio
        this.audioAssetPlayingId = a.id
        audio.onended = () => {
          this.isAudioAssetPlaying = false
          this.audioAssetPreviewInstance = null
          this.audioAssetPlayingId = null
        }
        audio.play().then(() => {
          this.isAudioAssetPlaying = true
        }).catch(() => {
          showErrorToast('音频试听失败')
        })
      } catch (e) {
        showErrorToast('无法试听音频')
      }
    },
    stopAudioPreview() {
      if (this.audioInstance) {
        this.audioInstance.pause()
        this.audioInstance = null
      }
      this.audioPlaying = false
    },
    stopRefAudioPreview() {
      if (this.refAudioInstance) {
        this.refAudioInstance.pause()
        this.refAudioInstance = null
      }
      this.refAudioPlaying = false
    },
    stopAudioAssetPreview() {
      if (this.audioAssetPreviewInstance) {
        this.audioAssetPreviewInstance.pause()
        this.audioAssetPreviewInstance = null
      }
      this.isAudioAssetPlaying = false
      this.audioAssetPlayingId = null
    },
    stopAllAudioPlayback() {
      this.stopAudioPreview()
      this.stopRefAudioPreview()
      this.stopAudioAssetPreview()
    },

    async handleGenerateVideo() {
      if (this.videoLoading || !this.canGenerateVideo) return
      this.videoLoading = true
      this.generateProgress = ''
      this.currentTaskId = ''
      try {
        if (this.driveMode === 'full') {
          let durationSec = Number(this.generatedAudioDurationSec) || 0
          if (durationSec < 1 && this.generatedAudioUrl) {
            durationSec = await probeAudioDurationSec(this.generatedAudioUrl)
            this.generatedAudioDurationSec = durationSec
          }
          if (durationSec < 1) {
            showErrorToast('无法读取配音时长，请重新生成配音后再提交')
            return
          }
          const needCredits = fullDriveCredits(this.modelCatalog, this.fullDriveMode, durationSec)
          await assertDigitalHumanCredits(needCredits, '全驱动数字人')
          const imageUrl = this.selectedAvatar.imageUrl || this.selectedAvatar.coverUrl
          const title = this.scriptText.trim().slice(0, 80) || '全驱动数字人'
          const { taskId } = await submitImageHumanFullDrive({
            imageUrl,
            audioUrl: this.generatedAudioUrl,
            prompt: this.fullDrivePrompt,
            title,
            mode: this.fullDriveMode,
            duration: durationSec
          })
          this.currentTaskId = taskId
          this.projectTab = 'all'
          this.projectSearch = ''
          const nowIso = new Date().toISOString()
          // 直接写入并赋给列表，保证右侧立刻出现「生成中」
          const nextList = upsertDigitalHumanTask({
            taskId,
            driveMode: 'full',
            title,
            cover: imageUrl,
            status: 'processing',
            createdAt: nowIso,
            updatedAt: nowIso
          })
          this.localTasks = Array.isArray(nextList) ? nextList.slice() : loadDigitalHumanTasks()
          showSuccessToast('任务已提交，正在生成')
          this.$nextTick(() => {
            if (this._destroyed) return
            // 再拉一次服务端合并，防止被并发 bootstrap 覆盖
            this.localTasks = loadDigitalHumanTasks()
            resumeTaskPolling(taskId, {
              onUpdate: (tasks) => this.onTasksUpdated(tasks)
            })
          })
        } else {
          const legacyId = 'legacy-' + Date.now()
          this.projectTab = 'all'
          this.projectSearch = ''
          this.localTasks = upsertDigitalHumanTask({
            taskId: legacyId,
            driveMode: 'legacy',
            title: this.scriptText.trim().slice(0, 80) || '口型驱动',
            cover: this.selectedAvatar.coverUrl || '',
            status: 'processing',
            createdAt: new Date().toISOString()
          })
          const url = await createDigitalHumanVideo({
            videoUrl: this.selectedAvatar.videoUrl,
            audioUrl: this.generatedAudioUrl,
            text: this.scriptText.trim()
          })
          if (url) {
            this.lastResultUrl = url
            this.previewUrl = url
            this.localTasks = upsertDigitalHumanTask({
              taskId: legacyId,
              status: 'completed',
              resultUrl: url,
              shuzirenUrl: url
            })
            showSuccessToast('视频生成成功')
          } else {
            this.localTasks = upsertDigitalHumanTask({
              taskId: legacyId,
              status: 'processing'
            })
            showSuccessToast('任务已提交，请稍后在作品列表查看')
          }
        }
      } catch (e) {
        const msg = String((e && e.message) || '').trim()
        // 提交其实已成功、仅解析失败时：拉服务端列表兜底
        if (/success|未解析到 task_id|未返回 task_id/i.test(msg)) {
          try {
            const list = await pullDigitalHumanTasksFromServer()
            this.projectTab = 'all'
            this.projectSearch = ''
            this.localTasks = Array.isArray(list) && list.length ? list : loadDigitalHumanTasks()
            const newest = (this.localTasks || []).find((t) => isTaskInProgress(t.status))
            if (newest && newest.taskId) {
              this.currentTaskId = newest.taskId
              resumeTaskPolling(newest.taskId, {
                onUpdate: (tasks) => this.onTasksUpdated(tasks)
              })
              showSuccessToast('任务已提交，正在生成')
              return
            }
          } catch (pullErr) {
            /* fall through */
          }
        }
        if (this.currentTaskId) {
          this.localTasks = upsertDigitalHumanTask({
            taskId: this.currentTaskId,
            status: 'failed',
            error: msg || ''
          })
        }
        showErrorToast(
          !msg || /^success$/i.test(msg) ? '提交失败，请稍后重试或点击右侧刷新' : msg
        )
      } finally {
        this.videoLoading = false
        this.generateProgress = ''
      }
    },
    previewVideo(url) {
      this.previewUrl = url
    }
  }
}
</script>

<style scoped>
.dh-page {
  --dh-page: #07070a;
  --dh-workflow-bg: rgba(255, 255, 255, 0.02);
  --dh-surface: rgba(255, 255, 255, 0.03);
  --dh-surface-2: rgba(0, 0, 0, 0.25);
  --dh-surface-3: rgba(0, 0, 0, 0.35);
  --dh-border: rgba(255, 255, 255, 0.1);
  --dh-border-soft: rgba(255, 255, 255, 0.08);
  --dh-border-ghost: rgba(255, 255, 255, 0.12);
  --dh-text: #f5f5f5;
  --dh-text-2: #e5e5e5;
  --dh-text-3: #d4d4d4;
  --dh-text-muted: #9ca3af;
  --dh-text-faint: #6b7280;
  --dh-success: #34d399;
  --dh-mode-wrap-bg: rgba(0, 0, 0, 0.3);
  --dh-mode-btn: #9ca3af;
  --dh-ghost-bg: rgba(255, 255, 255, 0.05);
  --dh-ghost-hover: rgba(255, 255, 255, 0.1);
  --dh-input-bg: rgba(0, 0, 0, 0.25);
  --dh-input-clear: rgba(255, 255, 255, 0.08);
  --dh-tab-bg: transparent;
  --dh-tab-active-bg: rgba(255, 255, 255, 0.1);
  --dh-tab-active-border: rgba(255, 255, 255, 0.15);
  --dh-modal-mask: rgba(0, 0, 0, 0.72);
  --dh-modal-bg: #121218;
  --dh-asset-bg: #181820;
  --dh-cookie-bg: rgba(15, 23, 42, 0.35);
  --dh-cookie-border: rgba(148, 163, 184, 0.15);
  --dh-projects-head-bg: linear-gradient(180deg, rgba(255, 255, 255, 0.03), transparent);
  --dh-search-bg: rgba(0, 0, 0, 0.28);
  --dh-card-bg: linear-gradient(160deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0.02) 100%);
  --dh-card-border: rgba(255, 255, 255, 0.09);
  --dh-card-shadow: 0 4px 20px rgba(0, 0, 0, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.06);
  --dh-card-hover-shadow: 0 16px 40px rgba(0, 0, 0, 0.38), 0 0 0 1px rgba(139, 92, 246, 0.12);
  --dh-card-media: linear-gradient(180deg, #12121a 0%, #050508 100%);
  --dh-card-body-bg: linear-gradient(180deg, rgba(255, 255, 255, 0.03), transparent);
  --dh-card-body-border: rgba(255, 255, 255, 0.06);
  --dh-card-title: #f3f4f6;
  --dh-card-footer-border: rgba(255, 255, 255, 0.07);
  --dh-action-bg: rgba(124, 58, 237, 0.12);
  --dh-action-color: #c4b5fd;
  --dh-action-border: rgba(139, 92, 246, 0.35);
  --dh-sm-bg: linear-gradient(135deg, rgba(99, 102, 241, 0.18), rgba(139, 92, 246, 0.12));
  --dh-sm-color: #e9d5ff;
  --dh-sm-border: rgba(139, 92, 246, 0.45);
  --dh-sm-rewrite-bg: linear-gradient(135deg, rgba(124, 58, 237, 0.16), rgba(219, 39, 119, 0.1));
  --dh-sm-rewrite-color: #fbcfe8;
  --dh-sm-rewrite-border: rgba(236, 72, 153, 0.4);
  --dh-dots: rgba(255, 255, 255, 0.08);
  --dh-theme-btn-bg: rgba(255, 255, 255, 0.06);
  --dh-theme-btn-color: #d1d5db;
  --dh-preview-hint-bg: rgba(0, 0, 0, 0.62);
  color: var(--dh-text);
  transition: color 0.22s ease;
}
.dh-page[data-theme='light'] {
  --dh-page: #f4f5f7;
  --dh-workflow-bg: #ffffff;
  --dh-surface: #ffffff;
  --dh-surface-2: #f8fafc;
  --dh-surface-3: #f1f5f9;
  --dh-border: #e5e7eb;
  --dh-border-soft: #eceef2;
  --dh-border-ghost: #d1d5db;
  --dh-text: #111827;
  --dh-text-2: #1f2937;
  --dh-text-3: #374151;
  --dh-text-muted: #6b7280;
  --dh-text-faint: #9ca3af;
  --dh-success: #059669;
  --dh-mode-wrap-bg: #f3f4f6;
  --dh-mode-btn: #6b7280;
  --dh-ghost-bg: #f8fafc;
  --dh-ghost-hover: #eef2ff;
  --dh-input-bg: #f8fafc;
  --dh-input-clear: #e5e7eb;
  --dh-tab-bg: #ffffff;
  --dh-tab-active-bg: #eef2ff;
  --dh-tab-active-border: #c7d2fe;
  --dh-modal-mask: rgba(15, 23, 42, 0.4);
  --dh-modal-bg: #ffffff;
  --dh-asset-bg: #f8fafc;
  --dh-cookie-bg: #f8fafc;
  --dh-cookie-border: #e2e8f0;
  --dh-projects-head-bg: linear-gradient(180deg, #ffffff, transparent);
  --dh-search-bg: #ffffff;
  --dh-card-bg: #ffffff;
  --dh-card-border: #e5e7eb;
  --dh-card-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.05);
  --dh-card-hover-shadow: 0 12px 32px rgba(15, 23, 42, 0.1), 0 0 0 1px rgba(99, 102, 241, 0.12);
  --dh-card-media: linear-gradient(180deg, #eef2f7 0%, #e2e8f0 100%);
  --dh-card-body-bg: #ffffff;
  --dh-card-body-border: #f1f5f9;
  --dh-card-title: #111827;
  --dh-card-footer-border: #e5e7eb;
  --dh-action-bg: #eef2ff;
  --dh-action-color: #5b21b6;
  --dh-action-border: #c7d2fe;
  --dh-sm-bg: linear-gradient(135deg, #eef2ff, #f5f3ff);
  --dh-sm-color: #5b21b6;
  --dh-sm-border: #c7d2fe;
  --dh-sm-rewrite-bg: linear-gradient(135deg, #faf5ff, #fdf2f8);
  --dh-sm-rewrite-color: #9d174d;
  --dh-sm-rewrite-border: #fbcfe8;
  --dh-dots: rgba(15, 23, 42, 0.07);
  --dh-theme-btn-bg: #f3f4f6;
  --dh-theme-btn-color: #4b5563;
  --dh-preview-hint-bg: rgba(15, 23, 42, 0.55);
}
.dh-shell {
  background: var(--dh-page);
  color: var(--dh-text);
  transition: background-color 0.22s ease, color 0.22s ease;
}
.dh-shell__dots {
  background-image: radial-gradient(circle at 1px 1px, var(--dh-dots) 1px, transparent 1px);
  background-size: 22px 22px;
}
.dh-text-muted { color: var(--dh-text-muted); }
.dh-text-3 { color: var(--dh-text-3); }
.dh-text-faint { color: var(--dh-text-faint); }
.dh-text-success { color: var(--dh-success); }
.dh-theme-btn {
  width: 34px;
  height: 34px;
  border: 1px solid var(--dh-border);
  border-radius: 10px;
  background: var(--dh-theme-btn-bg);
  color: var(--dh-theme-btn-color);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.dh-theme-btn:hover {
  background: var(--dh-ghost-hover);
  color: var(--dh-text);
}
.dh-page[data-theme='dark'] .dh-theme-btn i.fa-sun {
  color: #fbbf24;
}
.dh-workflow,
.dh-workflow__head {
  border-color: var(--dh-border);
}
.dh-mode-wrap {
  border-color: var(--dh-border);
  background: var(--dh-mode-wrap-bg);
}
.dh-mode-btn {
  border: 0;
  background: transparent;
  color: var(--dh-mode-btn);
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.dh-mode-btn--active {
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  color: #fff;
}
.dh-workflow {
  background: var(--dh-workflow-bg);
}
.dh-step {
  border-radius: 14px;
  border: 1px solid var(--dh-border-soft);
  background: var(--dh-surface);
  padding: 14px;
}
.dh-step__head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--dh-text-2);
}
.dh-step__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  font-size: 11px;
  font-weight: 800;
}
.dh-avatar-preview {
  position: relative;
  width: 100%;
  height: 168px;
  border-radius: 12px;
  border: 1px solid var(--dh-border);
  background: var(--dh-surface-3);
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.15s ease;
}
.dh-avatar-preview:hover {
  border-color: rgba(139, 92, 246, 0.45);
}
.dh-avatar-preview__media {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: var(--dh-surface-2);
}
.dh-avatar-preview__hint {
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--dh-preview-hint-bg);
  border: 1px solid var(--dh-border-ghost);
  font-size: 10px;
  color: #fff;
  pointer-events: none;
}
.dh-textarea {
  width: 100%;
  resize: none;
  border-radius: 12px;
  border: 1px solid var(--dh-border);
  background: var(--dh-input-bg);
  padding: 12px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--dh-text);
  outline: none;
}
.dh-textarea:focus {
  border-color: rgba(139, 92, 246, 0.5);
}
.dh-textarea--cookie {
  min-height: 72px;
  max-height: 160px;
  resize: vertical;
  word-break: break-all;
}
.dh-input {
  width: 100%;
  border-radius: 12px;
  border: 1px solid var(--dh-border);
  background: var(--dh-input-bg);
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--dh-text);
  outline: none;
}
.dh-input-wrap {
  position: relative;
}
.dh-input--clearable {
  padding-right: 34px;
}
.dh-input-wrap__clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 6px;
  background: var(--dh-input-clear);
  color: var(--dh-text-muted);
  font-size: 11px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.dh-input-wrap__clear:hover {
  background: rgba(239, 68, 68, 0.18);
  color: #fca5a5;
}
.dh-input:focus {
  border-color: rgba(139, 92, 246, 0.5);
}
.dh-input::placeholder {
  color: var(--dh-text-faint);
}
.dh-btn-primary,
.dh-btn-accent,
.dh-btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  transition: opacity 0.15s ease, background 0.15s ease;
}
.dh-btn-primary {
  border: 0;
  color: #fff;
  background: #0ea5e9;
}
.dh-btn-primary:hover:not(:disabled) {
  background: #0284c7;
}
.dh-btn-accent {
  border: 0;
  color: #fff;
  background: linear-gradient(90deg, #7c3aed, #db2777);
}
.dh-btn-accent:hover:not(:disabled) {
  filter: brightness(1.06);
}
.dh-btn-ghost {
  border: 1px solid var(--dh-border-ghost);
  background: var(--dh-ghost-bg);
  color: var(--dh-text-3);
  white-space: nowrap;
  flex-shrink: 0;
}
.dh-btn-ghost--compact {
  padding: 7px 12px;
  font-size: 12px;
  line-height: 1.2;
  border-radius: 999px;
}
.dh-btn-ghost:hover {
  background: var(--dh-ghost-hover);
  color: var(--dh-text);
}
.dh-btn-primary:disabled,
.dh-btn-accent:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
/* AI 主按钮：生成配音 / 生成视频 */
.dh-btn-ai {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 46px;
  padding: 12px 18px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  overflow: hidden;
  isolation: isolate;
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
}
.dh-btn-ai::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0;
  background: linear-gradient(105deg, transparent 35%, rgba(255, 255, 255, 0.22) 50%, transparent 65%);
  transform: translateX(-120%);
  transition: transform 0.55s ease, opacity 0.2s ease;
  pointer-events: none;
}
.dh-btn-ai:hover:not(:disabled)::before {
  opacity: 1;
  transform: translateX(120%);
}
.dh-btn-ai--audio {
  background: linear-gradient(135deg, #0284c7 0%, #4f46e5 52%, #7c3aed 100%);
  box-shadow:
    0 6px 24px rgba(79, 70, 229, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.18);
}
.dh-btn-ai--video {
  background: linear-gradient(135deg, #6d28d9 0%, #9333ea 42%, #db2777 100%);
  box-shadow:
    0 6px 24px rgba(147, 51, 234, 0.38),
    inset 0 1px 0 rgba(255, 255, 255, 0.18);
}
.dh-btn-ai:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.06);
}
.dh-btn-ai--audio:hover:not(:disabled) {
  box-shadow:
    0 10px 32px rgba(79, 70, 229, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);
}
.dh-btn-ai--video:hover:not(:disabled) {
  box-shadow:
    0 10px 32px rgba(219, 39, 119, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);
}
.dh-btn-ai:active:not(:disabled) {
  transform: translateY(0);
}
.dh-btn-ai:disabled {
  opacity: 0.42;
  cursor: not-allowed;
  filter: saturate(0.65);
  box-shadow: none;
}
.dh-btn-ai__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: #fff;
  font-size: 13px;
  flex-shrink: 0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
}
.dh-btn-ai__svg {
  width: 18px;
  height: 18px;
  display: block;
  color: #fff;
}
.dh-btn-ai__label {
  flex: 1;
  text-align: center;
  letter-spacing: 0.02em;
}
.dh-btn-ai__tag {
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  flex-shrink: 0;
}
.dh-btn-ai__credits,
.dh-btn-ai-sm__credits {
  margin-left: 2px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  color: #78350f;
  background: linear-gradient(135deg, #fef3c7 0%, #fcd34d 100%);
  border: 1px solid rgba(245, 158, 11, 0.45);
  white-space: nowrap;
  flex-shrink: 0;
}
.dh-btn-ai-sm__credits {
  padding: 1px 6px;
  font-size: 9px;
}
/* AI 次级按钮：提取 / 改写 */
.dh-btn-ai-sm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid var(--dh-sm-border);
  background: var(--dh-sm-bg);
  color: var(--dh-sm-color);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}
.dh-btn-ai-sm--rewrite {
  border-color: var(--dh-sm-rewrite-border);
  background: var(--dh-sm-rewrite-bg);
  color: var(--dh-sm-rewrite-color);
}
.dh-btn-ai-sm__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  font-size: 10px;
}
.dh-btn-ai-sm:hover:not(:disabled) {
  border-color: rgba(167, 139, 250, 0.7);
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.32), rgba(139, 92, 246, 0.22));
  color: #fff;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.25);
}
.dh-btn-ai-sm--rewrite:hover:not(:disabled) {
  border-color: rgba(244, 114, 182, 0.65);
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.28), rgba(219, 39, 119, 0.2));
  box-shadow: 0 4px 16px rgba(219, 39, 119, 0.22);
}
.dh-btn-ai-sm:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.dh-tab {
  border: 1px solid var(--dh-border-soft);
  background: var(--dh-tab-bg);
  color: var(--dh-text-muted);
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
}
.dh-tab--active {
  background: var(--dh-tab-active-bg);
  color: var(--dh-text);
  border-color: var(--dh-tab-active-border);
}
.dh-modal {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--dh-modal-mask);
  padding: 16px;
}
.dh-modal__panel {
  width: min(720px, 100%);
  max-height: 80vh;
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid var(--dh-border);
  background: var(--dh-modal-bg);
  color: var(--dh-text);
  display: flex;
  flex-direction: column;
}
.dh-modal__panel--video {
  width: auto;
  max-width: min(920px, 96vw);
  max-height: 92vh;
  padding: 12px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: visible;
}
.dh-preview-video-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-height: calc(92vh - 48px);
}
.dh-preview-video {
  display: block;
  max-width: 100%;
  max-height: calc(92vh - 48px);
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 12px;
  background: #000;
}
.dh-modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--dh-border-soft);
}
.dh-modal__head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
}
.dh-modal__head button {
  border: 0;
  background: transparent;
  color: var(--dh-text-muted);
  cursor: pointer;
}
.dh-modal__body {
  padding: 16px;
  overflow-y: auto;
}
.dh-asset-card {
  position: relative;
  aspect-ratio: 3 / 4;
  overflow: hidden;
  border-radius: 14px;
  border: 1px solid var(--dh-border-soft);
  background: var(--dh-asset-bg);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.dh-asset-card:hover {
  border-color: rgba(99, 102, 241, 0.5);
}
.dh-asset-card--active {
  border-color: #818cf8;
  box-shadow: 0 0 0 1px rgba(129, 140, 248, 0.4);
}
.dh-asset-card--create {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--dh-text-3);
}
.dh-asset-card--audio {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
}
.dh-asset-card__name {
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 8px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  font-size: 11px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dh-audio-preview-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  border: 0;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 4px 8px;
  font-size: 10px;
  cursor: pointer;
}
.dh-audio-preview-btn:hover {
  background: rgba(14, 165, 233, 0.85);
}
.dh-preview-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  cursor: pointer;
}
.dh-douyin-cookie {
  border-radius: 8px;
  border: 1px solid var(--dh-cookie-border);
  background: var(--dh-cookie-bg);
  padding: 8px 10px;
}
.dh-douyin-cookie__summary {
  cursor: pointer;
  font-size: 11px;
  color: var(--dh-text-muted);
  list-style: none;
  display: flex;
  align-items: center;
  gap: 6px;
}
.dh-douyin-cookie__summary::-webkit-details-marker {
  display: none;
}
.dh-douyin-cookie[open] .dh-douyin-cookie__summary {
  color: var(--dh-text-3);
  margin-bottom: 4px;
}
/* 作品列表 */
.dh-projects-head {
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-bottom: 1px solid var(--dh-border-soft);
  padding: 16px 20px;
  background: var(--dh-projects-head-bg);
}
.dh-projects-head__title h2 {
  font-size: 18px;
  font-weight: 700;
  color: var(--dh-text);
}
.dh-projects-head__title p {
  margin-top: 4px;
  font-size: 12px;
  color: var(--dh-text-faint);
}
.dh-projects-toolbar {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
@media (min-width: 768px) {
  .dh-projects-toolbar {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}
.dh-projects-search {
  position: relative;
  flex: 1;
  max-width: 360px;
}
.dh-projects-search__icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: var(--dh-text-faint);
  pointer-events: none;
}
.dh-projects-search__input {
  width: 100%;
  height: 36px;
  padding: 0 34px 0 34px;
  border-radius: 999px;
  border: 1px solid var(--dh-border);
  background: var(--dh-search-bg);
  font-size: 12px;
  color: var(--dh-text);
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.dh-projects-search__input:focus {
  border-color: rgba(139, 92, 246, 0.45);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.12);
}
.dh-projects-search__input::placeholder {
  color: var(--dh-text-faint);
}
.dh-projects-search__clear {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 999px;
  background: var(--dh-input-clear);
  color: var(--dh-text-muted);
  cursor: pointer;
}
.dh-projects-search__clear:hover {
  background: var(--dh-ghost-hover);
  color: var(--dh-text);
}
.dh-projects-toolbar__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.dh-projects-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 64px 16px;
  text-align: center;
  color: var(--dh-text-faint);
  font-size: 13px;
}
.dh-projects-empty__icon {
  font-size: 28px;
  opacity: 0.35;
}
.dh-project-card {
  overflow: hidden;
  border-radius: 18px;
  border: 1px solid var(--dh-card-border);
  background: var(--dh-card-bg);
  box-shadow: var(--dh-card-shadow);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}
.dh-project-card:hover {
  transform: translateY(-3px);
  border-color: rgba(167, 139, 250, 0.4);
  box-shadow: var(--dh-card-hover-shadow);
}
.dh-project-card__media {
  position: relative;
  aspect-ratio: 16 / 9;
  background: var(--dh-card-media);
  overflow: hidden;
}
.dh-project-card__shade {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.08) 0%,
    transparent 38%,
    rgba(0, 0, 0, 0.55) 100%
  );
  pointer-events: none;
}
.dh-project-card__thumb {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: #000;
  transition: transform 0.35s ease;
}
.dh-project-card:hover .dh-project-card__thumb {
  transform: scale(1.03);
}
.dh-project-card__thumb--cover {
  object-fit: cover;
  opacity: 0.92;
}
.dh-project-card__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.12);
  font-size: 36px;
}
.dh-project-card__status {
  position: absolute;
  left: 10px;
  top: 10px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px 4px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.12);
}
.dh-project-card__status-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: currentColor;
  opacity: 0.9;
}
.dh-project-card__status--done {
  background: rgba(16, 185, 129, 0.22);
  color: #6ee7b7;
  border-color: rgba(16, 185, 129, 0.35);
}
.dh-project-card__status--pending {
  background: rgba(245, 158, 11, 0.2);
  color: #fcd34d;
  border-color: rgba(245, 158, 11, 0.35);
}
.dh-project-card__status--pending .dh-project-card__status-dot {
  animation: dh-pulse 1.4s ease-in-out infinite;
}
.dh-project-card__status--fail {
  background: rgba(244, 63, 94, 0.22);
  color: #fda4af;
  border-color: rgba(244, 63, 94, 0.35);
}
@keyframes dh-pulse {
  0%,
  100% {
    opacity: 0.45;
    transform: scale(0.92);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}
.dh-project-card__delete {
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 4;
  width: 30px;
  height: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  background: rgba(10, 10, 14, 0.62);
  backdrop-filter: blur(8px);
  color: #fca5a5;
  font-size: 11px;
  cursor: pointer;
  opacity: 0.82;
  transition: opacity 0.15s ease, transform 0.15s ease, background 0.15s ease, color 0.15s ease;
}
.dh-project-card:hover .dh-project-card__delete {
  opacity: 1;
}
.dh-project-card__delete:hover {
  background: rgba(244, 63, 94, 0.88);
  color: #fff;
  border-color: transparent;
  transform: scale(1.05);
}
.dh-project-card__overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.28);
  opacity: 0;
  transition: opacity 0.22s ease, background 0.22s ease;
}
.dh-project-card:hover .dh-project-card__overlay {
  opacity: 1;
  background: rgba(0, 0, 0, 0.42);
}
.dh-project-card__overlay--loading {
  opacity: 1;
  background: rgba(0, 0, 0, 0.5);
}
.dh-project-card__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
}
.dh-project-card__preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  color: #fff;
  cursor: pointer;
  padding: 0;
}
.dh-project-card__play {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.96);
  color: #7c3aed;
  font-size: 16px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.dh-project-card__play i {
  margin-left: 2px;
}
.dh-project-card__preview:hover .dh-project-card__play {
  transform: scale(1.08);
  box-shadow: 0 10px 32px rgba(124, 58, 237, 0.35);
}
.dh-project-card__preview-text {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
}
.dh-project-card__body {
  padding: 14px 14px 12px;
  border-top: 1px solid var(--dh-card-body-border);
  background: var(--dh-card-body-bg);
}
.dh-project-card__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--dh-card-title);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.9em;
}
.dh-project-card__footer {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--dh-card-footer-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.dh-project-card__meta {
  font-size: 11px;
  color: var(--dh-text-muted);
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}
.dh-project-card__action {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid var(--dh-action-border);
  background: var(--dh-action-bg);
  color: var(--dh-action-color);
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.dh-project-card__action:hover {
  background: rgba(124, 58, 237, 0.28);
  color: #fff;
  border-color: rgba(167, 139, 250, 0.55);
}
.dh-project-card__action--sync {
  border-color: rgba(56, 189, 248, 0.35);
  background: rgba(14, 165, 233, 0.12);
  color: #7dd3fc;
}
.dh-project-card__action--sync:hover:not(:disabled) {
  background: rgba(14, 165, 233, 0.24);
  color: #fff;
  border-color: rgba(56, 189, 248, 0.55);
}
.dh-project-card__action:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.dh-page[data-theme='light'] .dh-btn-ai-sm:hover:not(:disabled) {
  color: #4c1d95;
}
.dh-page[data-theme='light'] .dh-btn-ai-sm--rewrite:hover:not(:disabled) {
  color: #9d174d;
}
.dh-page[data-theme='light'] .dh-project-card__action:hover {
  background: #ddd6fe;
  color: #5b21b6;
}
.dh-page[data-theme='light'] .dh-project-card__status--done {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
  border-color: rgba(16, 185, 129, 0.28);
}
.dh-page[data-theme='light'] .dh-project-card__status--pending {
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
  border-color: rgba(245, 158, 11, 0.28);
}
.dh-page[data-theme='light'] .dh-project-card__status--fail {
  background: rgba(244, 63, 94, 0.1);
  color: #be123c;
  border-color: rgba(244, 63, 94, 0.25);
}
.dh-page[data-theme='light'] .dh-project-card__placeholder {
  color: rgba(15, 23, 42, 0.18);
}
</style>
